import _ from 'lodash'
import { useState, useCallback, useEffect } from 'react'
import { useSelector, connect, useDispatch } from 'react-redux'
import { Avatar, Layout, Menu, Modal } from 'antd'
import {
    ReconciliationOutlined,
    AppstoreOutlined,
    ArrowLeftOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
    LockFilled,
    SearchOutlined,
    SnippetsOutlined,
    TeamOutlined,
    ToolOutlined,
} from '@ant-design/icons'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import { selectFlags } from '@Teams/selectors'
import { BORDER } from '@User/MyAccountPage/styleConstants'
import { useEnableAutoOtUI } from '@OpsWb/logic'

import ItarModal from '~/apps/consumer/components/ItarModal'
import {
    FABRICATE,
    ADMIN_PATH,
    OPERATIONS,
    ADMIN,
    WORKBENCH_JOBS,
    WORKBENCH_JOBS_PATH,
    OPS_SCHEDULING_PATH,
    QUOTES_ORDERS,
    SEARCH,
    OPS_PRODUCTION_PATH,
} from '~/redux-modules/preferences'
import icon from '~/apps/consumer/icons'
import CreateTeamModal from '~/apps/consumer/components/CreateTeamModal/CreateTeamModal'
import { getUserIdentity } from '~/redux-modules/identity'
import { closeModal, getModal, openModal } from '~/redux-modules/modals'
import { getenv } from '~/settings'
import getFirstValueFromString from '~/lib/getFirstValueFromString'
import Button from '~/core/Button'
import Text from '~/core/Text'
import Image from '~/core/Image'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import PremiumTag from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/PremiumTag'
import Box from '~/core/Box'
import getColor from '~/lib/getColor'
import { openChatGetPremiumEnabled } from '~/redux-modules/drift'
import { CREATE_TEAM_MODAL } from '~/apps/consumer/components/CreateTeamModal/constants'
import { useUserTeamsQuery } from '~/gql/graphql'

import MenuItem, { navItemsType } from './MenuItem'
import styles from './styles.css'

type NewSidebarNavTypes = {
    collapsed: boolean
    sidebar?: string
    openModal: Function
}

function mapStateToProps() {
    return {
        openModal,
    }
}

const NewSidebarNav = (props: NewSidebarNavTypes) => {
    const { collapsed, sidebar } = props
    const {
        isAdmin,
        isImpersonating,
        isVendor,
        businessAccountMembership,
        id,
    } = useSelector(getUserIdentity)
    const dispatch = useDispatch()

    const { isAllWorkspacesFeatureEnabled } = useSelector(selectFlags)
    const [sidebarItem, setSidebarItem] = useState(sidebar)
    const isEarlyAccessTeamsEnabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.RELEASE_GET_EARLY_ACCESS_TEAMS,
    )
    const isTeamsFeatureEnabled =
        !!businessAccountMembership?.businessAccount?.isTeamsFeatureEnabled
    const handleSetSidebar = useCallback((appName: string) => {
        setSidebarItem(appName)
    }, [])

    useEffect(() => {
        setSidebarItem(sidebar)
    }, [sidebar])
    const businessAccountId = businessAccountMembership?.businessAccount?.id

    const { isOpen } = useSelector(state => {
        return getModal(state, CREATE_TEAM_MODAL)
    })

    const [createTeamModalVisible, setCreateTeamModalVisible] =
        useState<boolean>(false)
    const [isPremiumAccessModalVisible, setIsPremiumAccessModalVisible] =
        useState<boolean>(false)
    const enableAutoOTUI = useEnableAutoOtUI()

    const { data } = useUserTeamsQuery({
        variables: {
            userId: id,
        },
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        if (isOpen) {
            setCreateTeamModalVisible(true)
        }
    }, [isOpen, setCreateTeamModalVisible])

    const teams = data?.user?.teams?.items || []

    const createTeamTestTarget = isTeamsFeatureEnabled
        ? 'slider-menu-create-new-team'
        : 'slider-menu-premium-upsell-create-new-team'

    const defaultNavItems: navItemsType[] = [
        {
            to: '/pages/quotes',
            label: QUOTES_ORDERS,
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    src={<SnippetsOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: FABRICATE,
        },
    ]

    const navItems: navItemsType[] = []
    const adminNavItems: navItemsType[] = []
    const vendorNavItems: navItemsType[] = []
    const teamsNavItems: navItemsType[] = []
    const disabledTeamsNavItems: navItemsType[] = []

    if (isVendor) {
        vendorNavItems.push({
            href: `${getenv('WB_DOMAIN')}${WORKBENCH_JOBS_PATH}`,
            label: 'Workbench',
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    icon={<ToolOutlined />}
                />
            ),
            target: '_blank',
            placement: 'side_nav',
            navKey: WORKBENCH_JOBS,
        })
    }

    if (isAdmin) {
        adminNavItems.push({
            to: '/admin/app/search',
            label: 'Search',
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    icon={<SearchOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: SEARCH,
        })
        adminNavItems.push({
            to: '/admin/app',
            label: 'Internal tools',
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    icon={<AppstoreOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: ADMIN,
        })
        adminNavItems.push({
            to: enableAutoOTUI
                ? `${OPS_PRODUCTION_PATH}/salesOrders`
                : `${OPS_SCHEDULING_PATH}/unbundledProducts`,
            label: 'Operations workbench',
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    icon={<ReconciliationOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: OPERATIONS,
        })
    }

    if (isTeamsFeatureEnabled) {
        if (isAllWorkspacesFeatureEnabled) {
            teamsNavItems.push({
                to: '/pages/team/workspaces',
                label: 'All workspaces',
                icon: (
                    <Avatar
                        className={`${styles.avatar}`}
                        shape="square"
                        icon={<TeamOutlined />}
                    />
                ),
                placement: 'side_nav',
                navKey: 'All workspaces',
            })
        }

        teams.map(team => {
            teamsNavItems.push({
                to: `/pages/team/${team.id}`,
                label: team.name,
                placement: 'side_nav',
                navKey: team.name,
                icon: (
                    <Avatar className={styles.avatar}>
                        {getFirstValueFromString(team.name)}
                    </Avatar>
                ),
            })
        })
    } else {
        teams.map(team => {
            disabledTeamsNavItems.push({
                to: '#',
                label: (
                    <div
                        style={
                            !collapsed
                                ? { color: getColor('blackAlpha25') }
                                : { color: 'white' }
                        }
                    >
                        {team.name}
                    </div>
                ),
                placement: 'side_nav',
                navKey: team.name,
                icon: (
                    <Avatar
                        className={`${styles.disabledAvatar}`}
                        shape="square"
                    >
                        {getFirstValueFromString(team.name)}
                    </Avatar>
                ),
                onClick: evt => {
                    evt?.preventDefault()
                },
            })
        })
    }

    // This should be last for the user to see. impersonation is a special case
    // Fictiv admins and vendors should not see this unless in a teams is enabled
    if (isEarlyAccessTeamsEnabled || isTeamsFeatureEnabled) {
        navItems.push({
            to: '#',
            label: (
                <div style={{ marginTop: '-2px' }}>
                    <Text>Create a workspace</Text>
                </div>
            ),
            icon: (
                <Avatar
                    className={`${styles.lastAvatar}`}
                    shape="square"
                    icon={<PlusOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: ADMIN_PATH,
            dataTestTarget: createTeamTestTarget,
            onClick: evt => {
                evt?.preventDefault()
                if (isTeamsFeatureEnabled) {
                    setCreateTeamModalVisible(true)
                }
                if (!isTeamsFeatureEnabled) {
                    props.openModal('UPSELL')
                }
            },
        })
    }

    if (isImpersonating) {
        navItems.push({
            href: '/admin/users/0/stop_impersonating',
            label: 'Back to Admin',
            icon: (
                <Avatar
                    className={styles.avatar}
                    shape="square"
                    icon={<ArrowLeftOutlined />}
                />
            ),
            placement: 'side_nav',
            navKey: 'stop-impersonating',
        })
    }

    return (
        <Layout.Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={230}
            collapsedWidth={64}
            style={{
                background: '#fff',
                borderRight: BORDER,
            }}
        >
            <Menu
                theme="light"
                mode="inline"
                inlineIndent={0}
                className={styles.sliderMenu}
                selectedKeys={[(sidebarItem || sidebar) as string]}
            >
                <Menu.ItemGroup
                    title={
                        <>
                            {!collapsed ? (
                                <Text className={styles.navHeader}>
                                    My workspace
                                </Text>
                            ) : (
                                <hr
                                    style={{
                                        width: '70%',
                                        height: '1px',
                                        marginTop: '10px',
                                        marginBottom: '10px',
                                        opacity: '.5',
                                    }}
                                />
                            )}
                        </>
                    }
                >
                    {defaultNavItems.map(item => (
                        <Menu.Item
                            key={item.navKey}
                            icon={item.icon}
                            data-test-target={item.dataTestTarget}
                        >
                            <MenuItem
                                {...item}
                                id={id}
                                handleSetSidebar={handleSetSidebar}
                                collapsed={collapsed}
                            />
                        </Menu.Item>
                    ))}
                </Menu.ItemGroup>
                {(isAdmin || isVendor) && (
                    <Menu.ItemGroup
                        title={
                            <>
                                {!collapsed ? (
                                    <Text className={styles.navHeader}>
                                        Apps
                                    </Text>
                                ) : (
                                    <hr
                                        style={{
                                            width: '70%',
                                            height: '1px',
                                            marginTop: '10px',
                                            marginBottom: '10px',
                                            opacity: '.5',
                                        }}
                                    />
                                )}
                            </>
                        }
                    >
                        {isAdmin &&
                            adminNavItems.map(item => (
                                <Menu.Item
                                    key={item.navKey}
                                    icon={item.icon}
                                    data-test-target={item.dataTestTarget}
                                >
                                    <MenuItem
                                        {...item}
                                        id={id}
                                        handleSetSidebar={handleSetSidebar}
                                        collapsed={collapsed}
                                    />
                                </Menu.Item>
                            ))}
                        {isVendor &&
                            vendorNavItems.map(item => (
                                <Menu.Item
                                    key={item.navKey}
                                    icon={item.icon}
                                    data-test-target={item.dataTestTarget}
                                >
                                    <MenuItem
                                        {...item}
                                        id={id}
                                        handleSetSidebar={handleSetSidebar}
                                        collapsed={collapsed}
                                    />
                                </Menu.Item>
                            ))}
                    </Menu.ItemGroup>
                )}
                <Menu.ItemGroup
                    title={
                        <>
                            {!collapsed ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Text className={styles.premiumNavHeader}>
                                        Team workspaces
                                    </Text>
                                    <PremiumTag />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <hr
                                        style={{
                                            width: '20%',
                                            height: '1px',
                                            marginTop: '10px',
                                            opacity: '.5',
                                            marginRight: '3px',
                                        }}
                                    />
                                    {isTeamsFeatureEnabled ? (
                                        <Image
                                            style={{
                                                height: '16px',
                                                width: '16px',
                                            }}
                                            src={icon.premiumTagImg}
                                        />
                                    ) : (
                                        <LockFilled
                                            style={{
                                                fontSize: '16px',
                                                color: getColor(
                                                    'premiumBrandingTxt',
                                                ),
                                            }}
                                        />
                                    )}
                                    <hr
                                        style={{
                                            width: '20%',
                                            height: '1px',
                                            marginTop: '10px',
                                            opacity: '.5',
                                            marginLeft: '3px',
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    }
                >
                    {isTeamsFeatureEnabled ? (
                        <>
                            {teamsNavItems.map(item => (
                                <Menu.Item
                                    key={item.navKey}
                                    icon={item.icon}
                                    data-test-target={item.dataTestTarget}
                                >
                                    <MenuItem
                                        {...item}
                                        id={id}
                                        handleSetSidebar={handleSetSidebar}
                                        collapsed={collapsed}
                                    />
                                </Menu.Item>
                            ))}
                        </>
                    ) : (
                        <>
                            {disabledTeamsNavItems.map(item => (
                                <Menu.Item
                                    key={item.navKey}
                                    icon={item.icon}
                                    data-test-target={item.dataTestTarget}
                                    onClick={() => {
                                        setIsPremiumAccessModalVisible(true)
                                    }}
                                >
                                    <MenuItem
                                        {...item}
                                        id={id}
                                        handleSetSidebar={handleSetSidebar}
                                        collapsed={collapsed}
                                    />
                                </Menu.Item>
                            ))}
                        </>
                    )}
                    {navItems.map(item => (
                        <Menu.Item
                            key={item.navKey}
                            icon={item.icon}
                            data-test-target={item.dataTestTarget}
                        >
                            <MenuItem
                                {...item}
                                id={id}
                                handleSetSidebar={handleSetSidebar}
                                collapsed={collapsed}
                            />
                        </Menu.Item>
                    ))}
                </Menu.ItemGroup>
            </Menu>
            {isPremiumAccessModalVisible && (
                <Modal
                    className={styles.premiumModal}
                    visible={isPremiumAccessModalVisible}
                    onCancel={() => setIsPremiumAccessModalVisible(false)}
                    closable={false}
                    footer={
                        <Box justifyContent="end" borderTop="0px">
                            <Button
                                to={`/pages/quotes`}
                                onClick={() =>
                                    setIsPremiumAccessModalVisible(false)
                                }
                            >
                                Return to My Quotes &amp; Orders
                            </Button>
                            <Button
                                type="primary"
                                onClick={(evt: Event) => {
                                    evt.preventDefault()
                                    dispatch(openChatGetPremiumEnabled())
                                    setIsPremiumAccessModalVisible(false)
                                }}
                            >
                                Request upgrade
                            </Button>
                        </Box>
                    }
                >
                    <Box direction="row">
                        <Box>
                            <ExclamationCircleOutlined
                                style={{
                                    color: getColor('error'),
                                    fontSize: '21px',
                                }}
                            />
                        </Box>
                        <Box
                            style={{ fontSize: '16px' }}
                            alignItems="start"
                            direction="col"
                            width="432px"
                            paddingLeft="16px"
                        >
                            <Text
                                fontSize="16px"
                                fontWeight={500}
                                style={{ marginBottom: '8px' }}
                                color="blackAlpha65"
                            >
                                You've lost access to Fictiv Premium
                            </Text>
                            <Text
                                fontSize="14px"
                                color="blackAlpha65"
                                fontWeight={300}
                            >
                                Your team’s quotes &amp; orders have been saved,
                                but you’ll need to upgrade to Premium to regain
                                access to this team workspace. To connect with
                                our team, request an upgrade below.
                            </Text>
                        </Box>
                    </Box>
                </Modal>
            )}
            {createTeamModalVisible && (
                <CreateTeamModal
                    visible={createTeamModalVisible}
                    onCancel={() => {
                        setCreateTeamModalVisible(false)
                        dispatch(closeModal(CREATE_TEAM_MODAL))
                    }}
                    userId={id}
                    businessAccountMembershipId={businessAccountId}
                />
            )}
            <ItarModal />
        </Layout.Sider>
    )
}

export default _.flowRight(connect(mapStateToProps, { openModal }))(
    NewSidebarNav,
)
